import React from 'react';
import { theme } from 'antd';

export type SettingsPillBadgeTone = 'default' | 'processing' | 'success' | 'warning' | 'error';

// Was a hardcoded hex-per-tone map -- switched to the app's own theme tokens
// (same idea as creamyTokens' pillToneStyle, extended with 'processing' via
// antd's colorInfo* since that tone -- "Pending" verification/payout status
// -- doesn't fit success/warning/error) so this badge (and every screen that
// renders one, e.g. host verification / Stripe payout status) tracks the
// theme instead of a color frozen at write time.
const getToneColors = (tone: SettingsPillBadgeTone, token: ReturnType<typeof theme.useToken>['token']): { bg: string; fg: string } => {
  switch (tone) {
    case 'success':
      return { bg: token.colorSuccessBg, fg: token.colorSuccess };
    case 'warning':
      return { bg: token.colorWarningBg, fg: token.colorWarning };
    case 'error':
      return { bg: token.colorErrorBg, fg: token.colorError };
    case 'processing':
      return { bg: token.colorInfoBg, fg: token.colorInfo };
    default:
      return { bg: token.colorFillTertiary, fg: token.colorTextSecondary };
  }
};

interface SettingsPillBadgeProps {
  tone?: SettingsPillBadgeTone;
  children: React.ReactNode;
}

/** Small rounded status pill (verification/payout status, etc.) replacing antd's squared Tag in the redesigned Settings page. */
const SettingsPillBadge: React.FC<SettingsPillBadgeProps> = ({ tone = 'default', children }) => {
  const { token } = theme.useToken();
  const { bg, fg } = getToneColors(tone, token);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: bg,
        color: fg,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
};

export default SettingsPillBadge;
